// A programme's subjects. Subjects live once, in the institute's subject
// registry; a programme only holds references to them.
//
// Tabs:
//   In this programme   the linked subjects — search, paged; admins can
//                       remove one (it stays in the registry)
//   Add from list       the whole registry, paged and searched on the server;
//                       tick subjects on any page, then add them in one go
//   Create new          a new subject: into the registry and this programme
//   Import from Excel   existing codes are linked, new ones created
// Everyone else sees only the first tab.
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { AlertCircle, BookOpen, Check, FileSpreadsheet, Loader2, Plus, Search, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { WorkspaceDialog } from "@/components/WorkspaceDialog";
import { DataPager, usePagedList } from "@/components/DataPager";
import { useInstituteLabels } from "@/lib/instituteLabels";
import { SubjectImportDialog } from "../SubjectRegistry/SubjectImportDialog";
import {
    type Programme, type ProgrammeSubject, type RegistrySubject,
    createSubjectInProgramme, linkProgrammeSubjects, listProgrammeSubjects, searchSubjects,
    unlinkProgrammeSubject, withLabels,
} from "./structureApi";

const PAGE_SIZE = 20;
type Tab = "linked" | "add" | "create" | "import";

type Props = {
    programme: Programme | null;   // null = closed
    canManage: boolean;
    onClose: () => void;
};

function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
    return (
        <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input className="h-9 pl-9" placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
            {value && (
                <button type="button" onClick={() => onChange("")} aria-label="Clear search"
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground">
                    <X className="h-3.5 w-3.5" />
                </button>
            )}
        </div>
    );
}

export function ProgrammeSubjectsDialog({ programme, canManage, onClose }: Props) {
    const L = useInstituteLabels();
    const subj = L.subjects.toLowerCase();
    const prog = L.programme.toLowerCase();

    const [ tab, setTab ] = useState<Tab>("linked");
    const [ subjects, setSubjects ] = useState<ProgrammeSubject[]>([]);
    const [ loading, setLoading ] = useState(false);
    const [ error, setError ] = useState<string | null>(null);
    const [ notice, setNotice ] = useState<string | null>(null);
    const [ busyId, setBusyId ] = useState<string | null>(null);

    // add from list: one server page at a time; ticks are kept across pages
    const [ query, setQuery ] = useState("");
    const [ page, setPage ] = useState(1);
    const [ results, setResults ] = useState<RegistrySubject[]>([]);
    const [ total, setTotal ] = useState(0);
    const [ searching, setSearching ] = useState(false);
    const [ picked, setPicked ] = useState<Map<string, RegistrySubject>>(new Map());
    const [ linking, setLinking ] = useState(false);
    const searchSeq = useRef(0);

    // create new
    const [ code, setCode ] = useState("");
    const [ name, setName ] = useState("");
    const [ creating, setCreating ] = useState(false);

    const [ importOpen, setImportOpen ] = useState(false);

    const linked = usePagedList(subjects, (s, q) =>
        s.subjectCode.toLowerCase().includes(q) || (s.subjectName || "").toLowerCase().includes(q), PAGE_SIZE);

    const load = useCallback(async () => {
        if (!programme) return;
        setLoading(true);
        setError(null);
        try {
            setSubjects(await withLabels(L, () => listProgrammeSubjects(programme.id)));
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [ programme, L ]);

    useEffect(() => {
        setTab("linked"); setSubjects([]); setNotice(null); setError(null);
        setQuery(""); setPage(1); setResults([]); setTotal(0); setPicked(new Map());
        setCode(""); setName("");
        load();
    }, [ programme ]);

    // a new search starts on page 1
    useEffect(() => { setPage(1); }, [ query ]);

    // Registry page: debounced while typing; only the latest answer is kept.
    useEffect(() => {
        if (!programme || !canManage || tab !== "add") return;
        const seq = ++searchSeq.current;
        setSearching(true);
        const t = setTimeout(async () => {
            try {
                const res = await searchSubjects(query.trim(), page, PAGE_SIZE);
                if (seq === searchSeq.current) { setResults(res.subjects); setTotal(res.total); }
            } catch {
                if (seq === searchSeq.current) { setResults([]); setTotal(0); }
            } finally {
                if (seq === searchSeq.current) setSearching(false);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [ query, page, tab, programme, canManage ]);

    if (!programme) return null;

    const inProgramme = new Set(subjects.map(s => s.id));
    const pickable = results.filter(r => !inProgramme.has(r.id));
    const allOnPagePicked = pickable.length > 0 && pickable.every(r => picked.has(r.id));

    const togglePick = (r: RegistrySubject) => setPicked(prev => {
        const next = new Map(prev);
        if (next.has(r.id)) next.delete(r.id); else next.set(r.id, r);
        return next;
    });
    const togglePage = () => setPicked(prev => {
        const next = new Map(prev);
        if (allOnPagePicked) pickable.forEach(r => next.delete(r.id));
        else pickable.forEach(r => next.set(r.id, r));
        return next;
    });

    const say = (msg: string) => { setError(null); setNotice(msg); };

    const handleLink = async () => {
        setLinking(true); setError(null); setNotice(null);
        try {
            const res = await withLabels(L, () => linkProgrammeSubjects(programme.id, [ ...picked.keys() ]));
            say(`${res.linked.length} added to ${programme.name}${res.failed.length ? `, ${res.failed.length} no longer exist` : ""}.`);
            setPicked(new Map());
            await load();
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLinking(false);
        }
    };

    const handleCreate = async () => {
        setCreating(true); setError(null); setNotice(null);
        try {
            await withLabels(L, () => createSubjectInProgramme(programme.id, code.trim(), name.trim() || null));
            say(`${code.trim()} created and added.`);
            setCode(""); setName("");
            await load();
        } catch (err: any) {
            setError(err.message);
        } finally {
            setCreating(false);
        }
    };

    const handleUnlink = async (s: ProgrammeSubject) => {
        setBusyId(s.id); setError(null); setNotice(null);
        try {
            await withLabels(L, () => unlinkProgrammeSubject(programme.id, s.id));
            say(`${s.subjectCode} removed from ${programme.name}. It is still in the ${subj} list.`);
            await load();
        } catch (err: any) {
            setError(err.message);
        } finally {
            setBusyId(null);
        }
    };

    const tabs = (
        <Tabs value={tab} onValueChange={(v) => { setTab(v as Tab); setNotice(null); setError(null); }}>
            <TabsList>
                <TabsTrigger value="linked">In this {prog} <Badge variant="secondary" className="ml-1.5 h-5 px-1.5 text-[10px]">{subjects.length}</Badge></TabsTrigger>
                {canManage && <TabsTrigger value="add">Add from list</TabsTrigger>}
                {canManage && <TabsTrigger value="create">Create new</TabsTrigger>}
                {canManage && <TabsTrigger value="import">Import from Excel</TabsTrigger>}
            </TabsList>
        </Tabs>
    );

    let footer: ReactNode = undefined;
    if (tab === "linked") {
        footer = <DataPager page={linked.page} pageSize={PAGE_SIZE} total={linked.total} onPage={linked.setPage} loading={loading} />;
    } else if (tab === "add") {
        footer = (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <DataPager page={page} pageSize={PAGE_SIZE} total={total} onPage={setPage} loading={searching} className="sm:flex-1" />
                <div className="flex items-center justify-end gap-2 sm:border-l sm:pl-4">
                    <span className="text-sm text-muted-foreground">{picked.size} selected</span>
                    {picked.size > 0 && <Button variant="ghost" size="sm" onClick={() => setPicked(new Map())}>Clear</Button>}
                    <Button size="sm" onClick={handleLink} disabled={linking || picked.size === 0} className="gap-1.5">
                        {linking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add to {prog}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <>
            <WorkspaceDialog
                icon={BookOpen}
                title={`${L.subjects} · ${programme.name}`}
                description={`${L.subjects} live once in the institute's ${subj} list; a ${prog} only refers to them.`}
                headerExtra={tabs}
                footer={footer}
                onClose={onClose}
            >
                {error && (
                    <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                        <AlertCircle className="h-4 w-4 shrink-0" /> {error}
                    </div>
                )}
                {notice && (
                    <div className="mb-4 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                        <Check className="h-4 w-4 shrink-0" /> {notice}
                    </div>
                )}

                {/* IN THIS PROGRAMME */}
                {tab === "linked" && (
                    <div className="space-y-3">
                        <SearchBox value={linked.query} onChange={linked.setQuery} placeholder={`Search this ${prog}'s ${subj}…`} />
                        {loading && subjects.length === 0 ? (
                            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
                        ) : linked.total === 0 ? (
                            <div className="flex flex-col items-center gap-3 py-16 text-center">
                                <BookOpen className="h-10 w-10 text-muted-foreground/20" />
                                <p className="text-sm text-muted-foreground">
                                    {subjects.length === 0 ? `No ${subj} in this ${prog} yet.` : `No ${subj} match “${linked.query}”.`}
                                </p>
                                {subjects.length === 0 && canManage && (
                                    <Button variant="outline" size="sm" onClick={() => setTab("add")}><Plus className="h-4 w-4" /> Add {subj}</Button>
                                )}
                            </div>
                        ) : (
                            <div className="overflow-hidden rounded-lg border">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-muted/30">
                                            <TableHead className="w-40 pl-4">Code</TableHead>
                                            <TableHead>Name</TableHead>
                                            {canManage && <TableHead className="w-32 pr-4"><span className="sr-only">Remove</span></TableHead>}
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {linked.rows.map(s => (
                                            <TableRow key={s.id}>
                                                <TableCell className="pl-4"><span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{s.subjectCode}</span></TableCell>
                                                <TableCell className="text-sm">{s.subjectName || "—"}</TableCell>
                                                {canManage && (
                                                    <TableCell className="pr-4 text-right">
                                                        <Button size="sm" variant="ghost" className="gap-1 text-muted-foreground hover:text-red-600" disabled={busyId === s.id}
                                                            onClick={() => handleUnlink(s)} title={`Remove from this ${prog} (stays in the ${subj} list)`}>
                                                            {busyId === s.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />} Remove
                                                        </Button>
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </div>
                )}

                {/* ADD FROM LIST */}
                {tab === "add" && (
                    <div className="space-y-3">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <SearchBox value={query} onChange={setQuery} placeholder={`Search all ${subj} by code or name…`} />
                            <p className="text-xs text-muted-foreground">Ticks are kept when you change page or search.</p>
                        </div>
                        {searching && results.length === 0 ? (
                            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
                        ) : results.length === 0 ? (
                            <p className="py-16 text-center text-sm text-muted-foreground">
                                {query ? `No ${subj} match “${query}”.` : `The ${subj} list is empty. Use Create new or Import from Excel.`}
                            </p>
                        ) : (
                            <div className={`overflow-hidden rounded-lg border ${searching ? "opacity-60" : ""}`}>
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-muted/30">
                                            <TableHead className="w-10 pl-4">
                                                <Checkbox checked={allOnPagePicked} disabled={pickable.length === 0} onCheckedChange={togglePage}
                                                    aria-label="Select every subject on this page" />
                                            </TableHead>
                                            <TableHead className="w-40">Code</TableHead>
                                            <TableHead>Name</TableHead>
                                            <TableHead className="w-40 pr-4 text-right">Status</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {results.map(r => {
                                            const already = inProgramme.has(r.id);
                                            return (
                                                <TableRow key={r.id} className={already ? "opacity-60" : "cursor-pointer"} onClick={() => !already && togglePick(r)}>
                                                    <TableCell className="pl-4" onClick={(e) => e.stopPropagation()}>
                                                        <Checkbox checked={already || picked.has(r.id)} disabled={already} onCheckedChange={() => togglePick(r)} />
                                                    </TableCell>
                                                    <TableCell><span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{r.subject_code}</span></TableCell>
                                                    <TableCell className="text-sm">{r.subject_name || "—"}</TableCell>
                                                    <TableCell className="pr-4 text-right">
                                                        {already
                                                            ? <Badge variant="secondary" className="text-[11px]">In this {prog}</Badge>
                                                            : picked.has(r.id) ? <Badge className="text-[11px]">Selected</Badge> : null}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </div>
                )}

                {/* CREATE NEW */}
                {tab === "create" && (
                    <div className="max-w-xl space-y-4">
                        <p className="text-sm text-muted-foreground">
                            The new {L.subject.toLowerCase()} goes into the institute's {subj} list and into {programme.name}.
                            If the code already exists, add it from the list instead.
                        </p>
                        <div className="space-y-1.5">
                            <Label htmlFor="new-subject-code">Code</Label>
                            <Input id="new-subject-code" placeholder="e.g. CS101" value={code} onChange={(e) => setCode(e.target.value)} />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="new-subject-name">Name <span className="text-muted-foreground">(optional)</span></Label>
                            <Input id="new-subject-name" placeholder="e.g. Data Structures" value={name} onChange={(e) => setName(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && code.trim() && !creating && handleCreate()} />
                        </div>
                        <Button onClick={handleCreate} disabled={creating || !code.trim()} className="gap-1.5">
                            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create and add
                        </Button>
                    </div>
                )}

                {/* IMPORT */}
                {tab === "import" && (
                    <div className="max-w-xl space-y-4">
                        <p className="text-sm text-muted-foreground">
                            Upload an Excel or CSV file. New codes are created; codes already in the institute's {subj} list are
                            added to {programme.name} without making a copy. The import runs in the background: check
                            “In this {prog}” after a moment.
                        </p>
                        <div className="flex gap-2">
                            <Button onClick={() => setImportOpen(true)} className="gap-1.5">
                                <FileSpreadsheet className="h-4 w-4" /> Choose file
                            </Button>
                            <Button variant="outline" onClick={() => { setTab("linked"); load(); }}>Check the list</Button>
                        </div>
                    </div>
                )}
            </WorkspaceDialog>

            <SubjectImportDialog
                open={importOpen}
                onClose={() => { setImportOpen(false); load(); }}
                programmeId={programme.id}
                programmeName={programme.name}
            />
        </>
    );
}
